import { createFileRoute, Link } from "@tanstack/react-router";
import type { ComponentProps } from "react";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { WorkspaceMark } from "@/components/app/workspace/workspace-mark";
import { ChevronRight } from "@/lib/icons";
import { usePolicy } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";

export const Route = createFileRoute("/_authenticated/ws/sarah/setup")({
  beforeLoad: ({ context }) => guardRoute(context.me, "sarah-calls"),
  component: SarahSetup,
});

/** Core names these eight screens by system terms; here each is named for the question it answers,
 * grouped by who answers, what she says and the line itself. The links open Core's screens
 * unchanged: a map, not a copy. */
const GROUPS: {
  title: string;
  blurb: string;
  items: { id: string; name: string; what: string }[];
}[] = [
  {
    title: "Who answers",
    blurb: "The people a caller can be put through to.",
    items: [
      {
        id: "sarah-staff",
        name: "People & extensions",
        what: "Who is on the desk, the three digits that ring them, and when they are on shift.",
      },
    ],
  },
  {
    title: "What she says",
    blurb: "How Sarah handles a call before anybody here hears about it.",
    items: [
      {
        id: "sarah-agent",
        name: "Her greeting and questions",
        what: "What she says when she picks up, what she asks, and when she puts somebody through.",
      },
      {
        id: "sarah-agent-test",
        name: "Try her out",
        what: "Have a conversation with her yourself before a family does.",
      },
      {
        id: "sarah-escalation",
        name: "Calls to treat as urgent",
        what: "Who or what gets through straight away, whatever else is happening.",
      },
      {
        id: "sarah-suppression",
        name: "Numbers to never put through",
        what: "Sales calls and robocalls she answers and handles without ringing anybody.",
      },
      {
        id: "sarah-optimizer",
        name: "Improve her answers",
        what: "Suggestions drawn from real calls. You read them and decide.",
      },
    ],
  },
  {
    title: "The line itself",
    blurb: "The number that rings, the hours it is answered, and the accounts behind it.",
    items: [
      {
        id: "sarah-facility-settings",
        name: "Number and hours",
        what: "Which number this community answers on, and when she picks up.",
      },
      {
        id: "sarah-org-settings",
        name: "Phone account",
        what: "The accounts that carry the line and give her a voice. Set once, rarely touched.",
      },
    ],
  },
];

function SarahSetup() {
  const { navigation } = usePolicy();
  const pathOf = (id: string) => navigation.find((entry) => entry.id === id)?.path;

  const groups = GROUPS.map((group) => ({
    ...group,
    items: group.items.flatMap((item) => {
      const path = pathOf(item.id);
      return path ? [{ ...item, path }] : [];
    }),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6">
      <PageHeader
        mark={<WorkspaceMark id="sarah" size={44} />}
        kicker="Sarah, Set up"
        title="How the front desk is set up"
        meta="Change these rarely. Everything about answering the phone is on this page."
      />

      {groups.length === 0 ? (
        <EmptyState
          title="Setting up the front desk is not something this account can do."
          description="Ask an administrator if Sarah's greeting, hours or roster need to change."
        />
      ) : (
        groups.map((group) => (
          <section key={group.title} className="space-y-2">
            <div>
              <h2 className="text-base font-semibold">{group.title}</h2>
              <p className="text-muted-foreground text-sm">{group.blurb}</p>
            </div>
            <ul className="grid-cols-[minmax(0,1fr)] grid gap-2 sm:grid-cols-2">
              {group.items.map((item) => (
                <li key={item.id}>
                  <Link
                    to={item.path as ComponentProps<typeof Link>["to"]}
                    className="group bg-card shadow-card hover:border-sarah/40 flex h-full items-start gap-3 rounded-xl border p-4 transition-colors"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium">{item.name}</span>
                      <span className="text-muted-foreground mt-0.5 block text-sm text-pretty">
                        {item.what}
                      </span>
                    </span>
                    <ChevronRight className="text-muted-foreground mt-0.5 size-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}
